import { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { Card, MotionNumber } from '../ui';
import { useRealtime } from '../../hooks/useRealtime';
import { useMyPmMarkets } from '../../hooks/useApiQuery';
import { formatUsdAbsAdaptive } from '../../lib/format';
import type { RealtimeEnvelope } from '../../lib/realtime';
import type { PmMarketForMe, PositionItem } from '../../lib/types';
import { ActiveTrade } from './ActiveTrade';
import { OceanStrip, type GuardTone } from './OceanStrip';

interface Props {
  /**
   * Open trades (any bot_kind) — used to (a) annotate chips that have
   * a live PM position with a status dot, and (b) swap the scanning
   * body for an ActiveTrade headless body when the currently-selected
   * market has a live position. HL positions are ignored here; the
   * Dashboard renders them separately as standalone ActiveTrade cards.
   */
  openPositions?: readonly PositionItem[];
}

interface PricePayload {
  source?: 'rtds' | 'pyth' | 'binance';
  /** Per-market price reported by the trading-engine oracle for this
   *  envelope's market. Preferred over the legacy ``btc`` field. */
  oracle_price?: string;
  /** Back-compat: present only on BTC envelopes for pre-multi-market
   *  webapp builds. We still read it as a fallback. */
  btc?: string;
  ptb?: string;
  up_bid?: string;
  down_bid?: string;
  epoch_id?: string;
  market_id?: string;
  symbol?: string;
}

const num = (s?: string) => (s ? Number(s) : null);

/** Next epoch boundary STRICTLY in the future. ``floor + len`` (not
 * ``ceil``) so that at an exact boundary we land on the NEXT one
 * instead of `now` (Codex P2 on PR #112 — `ceil(now/len)*len === now`
 * briefly showed 00:00 and pinned the window on the just-ended epoch). */
function nextEpochEndMs(now: number, epochLenMs: number): number {
  return Math.floor(now / epochLenMs) * epochLenMs + epochLenMs;
}

function fmtCountdown(msLeft: number): { mm: string; ss: string } {
  const total = Math.max(0, Math.floor(msLeft / 1000));
  const mm = String(Math.floor(total / 60)).padStart(2, '0');
  const ss = String(total % 60).padStart(2, '0');
  return { mm, ss };
}

function epochWindow(now: number, epochLenMs: number, locale: string): string {
  const end = nextEpochEndMs(now, epochLenMs);
  const start = end - epochLenMs;
  const fmt = (ms: number) =>
    new Date(ms).toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
    });
  return `${fmt(start)}–${fmt(end)}`;
}

/** Pick which market drives the live PTB card. Prefer BTC 5m for
 *  legacy continuity (the single-market era's hero), fall back to
 *  whichever market the registry surfaces first. The realtime feed
 *  now publishes per-market ticks (PR #311), so any picked market
 *  will have live PTB/price data — no longer BTC-only. */
function pickActiveMarket(markets: readonly PmMarketForMe[]): PmMarketForMe | null {
  if (markets.length === 0) return null;
  return (
    markets.find((m) => m.market_id === 'btc-5m') ??
    markets.find((m) => m.symbol === 'BTC') ??
    markets[0]
  );
}

// Brand SVGs for the known tickers; unknown symbols fall back to the
// first letter on a neutral chip so a freshly-added asset doesn't
// crash the avatar.
function SymbolIcon({ symbol, size }: { symbol: string; size: number }) {
  if (symbol === 'BTC') return <BtcIcon size={size} />;
  if (symbol === 'ETH') return <EthIcon size={size} />;
  if (symbol === 'SOL') return <SolIcon size={size} />;
  if (symbol === 'BNB') return <BnbIcon size={size} />;
  if (symbol === 'XRP') return <XrpIcon size={size} />;
  if (symbol === 'DOGE') return <DogeIcon size={size} />;
  if (symbol === 'HYPE') return <HypeIcon size={size} />;
  return (
    <span
      aria-hidden
      style={{ width: size, height: size, fontSize: Math.round(size * 0.42) }}
      className="inline-flex shrink-0 items-center justify-center rounded-full bg-pb-paper2 font-bold text-tg-text"
    >
      {(symbol[0] ?? '?').toUpperCase()}
    </span>
  );
}

function BtcIcon({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      aria-hidden
      className="shrink-0"
    >
      <circle cx="16" cy="16" r="16" fill="#F7931A" />
      <path
        fill="#FFFFFF"
        d="M21.6 14.2c.3-1.9-1.2-3-3.2-3.6l.6-2.6-1.6-.4-.6 2.5-1.3-.3.6-2.5-1.6-.4-.7 2.6-1-.3-2.2-.5-.4 1.7s1.2.3 1.2.3c.6.2.7.6.7.9l-1.7 6.8c-.1.2-.3.4-.6.3 0 0-1.2-.3-1.2-.3l-.8 1.8 2.1.5 1.1.3-.7 2.6 1.6.4.6-2.5 1.3.3-.6 2.5 1.6.4.7-2.6c2.7.5 4.8.3 5.6-2.1.7-2 0-3.1-1.4-3.8 1-.2 1.8-.9 2-2.3zM18.4 18.6c-.5 2-3.7.9-4.8.6l.9-3.4c1 .3 4.4.8 3.9 2.8zm.5-4.5c-.4 1.8-3.1.9-4 .6l.8-3.1c.9.2 3.6.7 3.2 2.5z"
      />
    </svg>
  );
}

function EthIcon({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      aria-hidden
      className="shrink-0"
    >
      <circle cx="16" cy="16" r="16" fill="#627EEA" />
      <g fill="#FFFFFF" fillRule="nonzero">
        <path opacity="0.6" d="M16.5 4v8.87L24 16.22z" />
        <path d="M16.5 4 9 16.22l7.5-3.35z" />
        <path opacity="0.6" d="M16.5 21.97V28L24 17.62z" />
        <path d="M16.5 28v-6.04L9 17.62z" />
        <path opacity="0.2" d="m16.5 20.57 7.5-4.35-7.5-3.35z" />
        <path opacity="0.6" d="m9 16.22 7.5 4.35v-7.7z" />
      </g>
    </svg>
  );
}

function SolIcon({ size }: { size: number }) {
  const gradId = useId();
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      aria-hidden
      className="shrink-0"
    >
      <defs>
        <linearGradient
          id={gradId}
          x1="4"
          y1="28"
          x2="28"
          y2="4"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#9945FF" />
          <stop offset="0.5" stopColor="#7C3AED" />
          <stop offset="1" stopColor="#14F195" />
        </linearGradient>
      </defs>
      <circle cx="16" cy="16" r="16" fill="#0B0F1A" />
      <g fill={`url(#${gradId})`}>
        <path d="M9.4 21.6h14.2c.3 0 .5.3.3.6l-2.1 2.4c-.1.2-.3.2-.5.2H7.1c-.3 0-.5-.3-.3-.6l2.1-2.4c.1-.2.3-.2.5-.2z" />
        <path d="M9.4 7.2h14.2c.3 0 .5.3.3.6l-2.1 2.4c-.1.2-.3.2-.5.2H7.1c-.3 0-.5-.3-.3-.6l2.1-2.4c.1-.2.3-.2.5-.2z" />
        <path d="M22.6 14.4H8.4c-.3 0-.5.3-.3.6l2.1 2.4c.1.2.3.2.5.2h14.2c.3 0 .5-.3.3-.6l-2.1-2.4c-.1-.2-.3-.2-.5-.2z" />
      </g>
    </svg>
  );
}

function BnbIcon({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      aria-hidden
      className="shrink-0"
    >
      <circle cx="16" cy="16" r="16" fill="#F3BA2F" />
      <g fill="#FFFFFF">
        <path d="M16 5.5 18.5 8 16 10.5 13.5 8z" />
        <path d="M24 13.5 26.5 16 24 18.5 21.5 16z" />
        <path d="M16 21.5 18.5 24 16 26.5 13.5 24z" />
        <path d="M8 13.5 10.5 16 8 18.5 5.5 16z" />
        <path d="M16 11.5 20.5 16 16 20.5 11.5 16z" />
      </g>
    </svg>
  );
}

function XrpIcon({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      aria-hidden
      className="shrink-0"
    >
      <circle cx="16" cy="16" r="16" fill="#23292F" />
      <path
        fill="#FFFFFF"
        d="M22.6 9h2.9l-6 5.9c-2 2-5.1 2-7.1 0L6.5 9h2.9l4.5 4.5c1.2 1.2 3.2 1.2 4.4 0L22.6 9zM9.4 23H6.5l6-5.9c2-2 5.1-2 7.1 0l6 5.9h-2.9l-4.6-4.5c-1.2-1.2-3.2-1.2-4.4 0L9.4 23z"
      />
    </svg>
  );
}

function DogeIcon({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      aria-hidden
      className="shrink-0"
    >
      <circle cx="16" cy="16" r="16" fill="#C2A633" />
      <path
        fill="#FFFFFF"
        d="M13 7.5h4.6c4.5 0 7.6 3.1 7.6 8.4 0 5.3-3.1 8.6-7.6 8.6H13V7.5zm3 3v11h1.6c2.8 0 4.5-2 4.5-5.6 0-3.5-1.7-5.4-4.5-5.4H16z"
      />
      <rect x="9.5" y="14.6" width="9.5" height="2.6" fill="#FFFFFF" />
    </svg>
  );
}

function HypeIcon({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      aria-hidden
      className="shrink-0"
    >
      <circle cx="16" cy="16" r="16" fill="#072723" />
      <path
        fill="#97FCE4"
        d="M6.5 12c1.7 0 2.6.7 3.4 1.7 1 1.2 1.8 1.9 3.5 1.9 1.6 0 2.5-.7 3.4-1.9.9-1 1.8-1.7 3.5-1.7s2.6.7 3.5 1.7c.9 1.2 1.8 1.9 3.4 1.9v3.4c-1.7 0-2.6-.7-3.4-1.7-1-1.2-1.8-1.9-3.5-1.9-1.6 0-2.5.7-3.5 1.9-.8 1-1.7 1.7-3.4 1.7-1.7 0-2.6-.7-3.5-1.7-.9-1.2-1.8-1.9-3.4-1.9V12z"
      />
    </svg>
  );
}

export function PriceToBeat({ openPositions = [] }: Props = {}) {
  const { t, i18n } = useTranslation();
  const marketsQuery = useMyPmMarkets();
  const markets = marketsQuery.data?.items ?? [];

  const openByMarket = useMemo(() => {
    const m = new Map<string, PositionItem>();
    for (const p of openPositions) {
      if (
        p.bot_kind === 'polymarket' &&
        p.market_id &&
        (p.status === 'open' || p.status === 'pending')
      ) {
        m.set(p.market_id, p);
      }
    }
    return m;
  }, [openPositions]);

  // Keep the most-recent tick per market so the live PTB card stays
  // populated when the active market changes (we don't have a chip
  // selector yet, but the structure is forward-compatible). Keyed by
  // ``market_id``; legacy envelopes without ``market_id`` get folded
  // under ``btc-5m`` so a downgraded trading-engine doesn't blank the
  // dashboard.
  const [ticksByMarket, setTicksByMarket] = useState<
    Record<string, PricePayload>
  >({});
  const [now, setNow] = useState(() => Date.now());
  const [selectedMarketId, setSelectedMarketId] = useState<string | null>(null);

  useRealtime('global:price', (env: RealtimeEnvelope) => {
    const payload = env.payload as PricePayload;
    const key = payload.market_id ?? 'btc-5m';
    setTicksByMarket((prev) => ({ ...prev, [key]: payload }));
  });

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Drop a stale override eagerly so that if admin removes a
  // user-picked market and later re-adds it, the hero stays on the
  // auto-default instead of silently snapping back to the old pick.
  useEffect(() => {
    if (
      selectedMarketId !== null &&
      markets.length > 0 &&
      !markets.some((m) => m.market_id === selectedMarketId)
    ) {
      setSelectedMarketId(null);
    }
  }, [markets, selectedMarketId]);

  const autoMarket = useMemo(() => pickActiveMarket(markets), [markets]);
  const selectedMarket = useMemo(
    () =>
      selectedMarketId
        ? markets.find((m) => m.market_id === selectedMarketId) ?? null
        : null,
    [markets, selectedMarketId],
  );
  const activeMarket = selectedMarket ?? autoMarket;
  const activeTick = activeMarket ? ticksByMarket[activeMarket.market_id] : undefined;
  const openForActive = activeMarket
    ? openByMarket.get(activeMarket.market_id) ?? null
    : null;
  // ``oracle_price`` is the per-market price published since PR #311;
  // ``btc`` is the legacy back-compat field still emitted on BTC
  // envelopes — fall back so a downgraded trading-engine that didn't
  // ship #311 still renders the BTC card.
  const price = num(activeTick?.oracle_price ?? activeTick?.btc);
  const ptb = num(activeTick?.ptb);
  const upBid = num(activeTick?.up_bid);
  const downBid = num(activeTick?.down_bid);
  const delta = price !== null && ptb !== null ? price - ptb : null;

  const locale = i18n.language || 'ru';
  const activeEpochLenMs = (activeMarket?.timeframe_sec ?? 300) * 1000;
  const { mm, ss } = fmtCountdown(nextEpochEndMs(now, activeEpochLenMs) - now);
  const window = epochWindow(now, activeEpochLenMs, locale);

  // Empty state: admin hasn't enrolled this user into any PM market.
  // Surface that clearly instead of an awkward half-rendered card with
  // "—" placeholders everywhere.
  if (marketsQuery.isSuccess && markets.length === 0) {
    return (
      <Card pad="md" variant="paper">
        <p className="pb-eyebrow">{t('dashboard.pm_markets_label')}</p>
        <p className="pb-caption mt-2">{t('dashboard.pm_markets_empty')}</p>
      </Card>
    );
  }

  return (
    <Card pad="md" variant="paper">
      {/* Multi-market chip-strip — tap a chip to scope the live PTB
          card below to that market. Toolbar-of-toggle-buttons pattern
          (``role="group"`` + ``aria-pressed`` per chip) rather than
          full ARIA tabs, which would oblige aria-controls + tabpanel
          + arrow-key navigation we haven't built. */}
      {markets.length > 1 ? (
        <div
          className="-mx-1 mb-3 flex gap-2 overflow-x-auto px-1 pb-1"
          role="group"
          aria-label={t('dashboard.pm_markets_label')}
        >
          {markets.map((m) => (
            <MarketChip
              key={m.market_id}
              market={m}
              now={now}
              active={m.market_id === activeMarket?.market_id}
              hasOpenTrade={openByMarket.has(m.market_id)}
              onSelect={() => setSelectedMarketId(m.market_id)}
            />
          ))}
        </div>
      ) : null}

      {/* Header — symbol icon + title + epoch window + countdown */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5 min-w-0">
          {activeMarket ? (
            <SymbolIcon symbol={activeMarket.symbol} size={36} />
          ) : null}
          <div className="min-w-0">
            <h3 className="text-[15px] font-semibold text-tg-text leading-tight">
              {activeMarket
                ? t('dashboard.ptb_title', { market: activeMarket.display_name })
                : t('dashboard.ptb_title_fallback')}
            </h3>
            <p className="pb-caption mt-0.5">{window}</p>
          </div>
        </div>
        <div className="flex shrink-0 items-baseline gap-1">
          <Tick value={mm} label={t('dashboard.ptb_min')} />
          <Tick value={ss} label={t('dashboard.ptb_sec')} />
        </div>
      </div>

      {/* Round prices — always visible. These are USD oracle figures
          (price-to-beat + live BTC), so they coexist with the trade
          body below (which is in YES-probability units, not USD). */}
      <div className="mt-4 grid grid-cols-2 gap-3">
        <PriceCell
          label={t('dashboard.ptb_label')}
          value={ptb}
          tone="neutral"
        />
        <PriceCell
          label={
            activeMarket
              ? t('dashboard.btc_now_label', { symbol: activeMarket.symbol })
              : t('dashboard.btc_now_label_fallback')
          }
          value={price}
          tone={
            delta !== null && delta > 0
              ? 'positive'
              : delta !== null && delta < 0
                ? 'negative'
                : 'neutral'
          }
          delta={delta}
        />
      </div>

      {openForActive ? (
        // Key by position id so that switching chips between two open
        // PM positions remounts the headless body — guarantees fresh
        // internal state (snapshot, serverAnchor) instead of relying
        // on prop-change reconciliation. Codex PR #329 P1.
        <ActiveTrade
          key={openForActive.id}
          position={openForActive}
          headless
        />
      ) : (
        <>
          <div className="mt-3 flex items-stretch gap-2">
            <Bid label={t('dashboard.up_bid')} value={upBid} tone="success" />
            <Bid label={t('dashboard.down_bid')} value={downBid} tone="danger" />
          </div>

          {activeTick === undefined ? (
            <p className="pb-caption mt-3">{t('dashboard.ptb_waiting')}</p>
          ) : null}
        </>
      )}

      <OceanStrip
        mode={openForActive ? 'guarding' : 'hunting'}
        guardSide={openForActive ? sideOf(openForActive) : undefined}
        guardTone={
          openForActive
            ? toneFromLivePnl(openForActive, upBid, downBid, activeEpochLenMs)
            : undefined
        }
        activeMarketId={activeMarket?.market_id}
      />
    </Card>
  );
}

function sideOf(p: PositionItem): 'up' | 'down' {
  return /up|long/i.test(p.side) ? 'up' : 'down';
}

/**
 * Tint the kit by current PnL sign — green when the trade is winning,
 * red when losing, neutral otherwise. Computation mirrors the PM PnL
 * formula in ActiveTrade.tsx: ``(notional / entry) * current_bid - notional``.
 * The bid we use is whichever side the user took (up or down). During
 * the awaiting-settle window (now is past opened_at + epoch length)
 * the global:price feed is publishing the *next* epoch's bids, so we
 * intentionally return ``neutral`` rather than tint with a fictional PnL.
 */
function toneFromLivePnl(
  p: PositionItem,
  upBid: number | null,
  downBid: number | null,
  epochLenMs: number,
): GuardTone {
  const openedMs = p.opened_at ? Date.parse(p.opened_at) : NaN;
  if (Number.isFinite(openedMs)) {
    const epochEnd =
      Math.floor(openedMs / epochLenMs) * epochLenMs + epochLenMs;
    if (Date.now() > epochEnd) return 'neutral';
  }
  const bid = sideOf(p) === 'up' ? upBid : downBid;
  if (bid === null || bid <= 0) return 'neutral';
  const notional = Number(p.notional_usdc);
  const entry = Number(p.entry_price);
  if (!Number.isFinite(notional) || notional <= 0) return 'neutral';
  if (!Number.isFinite(entry) || entry <= 0) return 'neutral';
  const pnl = (notional / entry) * bid - notional;
  if (pnl > 0.01) return 'positive';
  if (pnl < -0.01) return 'negative';
  return 'neutral';
}

function MarketChip({
  market,
  now,
  active,
  hasOpenTrade,
  onSelect,
}: {
  market: PmMarketForMe;
  now: number;
  active: boolean;
  hasOpenTrade: boolean;
  onSelect: () => void;
}) {
  const epochLenMs = market.timeframe_sec * 1000;
  const { mm, ss } = fmtCountdown(nextEpochEndMs(now, epochLenMs) - now);
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onSelect}
      className={clsx(
        'pb-focus-ring flex shrink-0 items-center gap-2 rounded-pb-md border px-2.5 py-1.5 transition-colors',
        active
          ? 'border-pb-accent bg-pb-accent-soft'
          : 'border-pb-border-soft bg-pb-paper hover:border-pb-accent/40',
      )}
    >
      <span aria-hidden className="relative inline-flex h-6 w-6 shrink-0">
        <SymbolIcon symbol={market.symbol} size={24} />
        {hasOpenTrade ? (
          <span
            aria-hidden
            className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-pb-success ring-2 ring-pb-paper"
          />
        ) : null}
      </span>
      <div className="flex flex-col leading-tight">
        <span className="text-[12px] font-medium text-tg-text">
          {market.display_name}
        </span>
        <span className="pb-numeral text-[10px] tabular-nums text-tg-hint">
          {mm}:{ss}
        </span>
      </div>
    </button>
  );
}

function Tick({ value, label }: { value: string; label: string }) {
  return (
    <div className="flex flex-col items-center">
      <span className="pb-numeral text-[22px] leading-none font-semibold text-pb-danger tabular-nums">
        {value}
      </span>
      <span className="pb-caption mt-0.5 uppercase tracking-wider text-[9px]">
        {label}
      </span>
    </div>
  );
}

function PriceCell({
  label,
  value,
  tone,
  delta,
}: {
  label: string;
  value: number | null;
  tone: 'positive' | 'negative' | 'neutral';
  delta?: number | null;
}) {
  return (
    <div>
      <div className="flex items-center gap-1.5">
        <p className="pb-eyebrow">{label}</p>
        {delta !== null && delta !== undefined && Math.abs(delta) >= 0.01 ? (
          <span
            className={clsx(
              'pb-numeral text-[11px] font-medium tabular-nums',
              tone === 'positive' && 'text-pb-success',
              tone === 'negative' && 'text-pb-danger',
            )}
          >
            {delta > 0 ? '▲' : '▼'} {formatUsdAbsAdaptive(delta)}
          </span>
        ) : null}
      </div>
      {value !== null ? (
        <p
          className={clsx(
            'pb-numeral mt-1 text-[22px] font-semibold tabular-nums leading-tight',
            tone === 'positive' && 'text-pb-success',
            tone === 'negative' && 'text-pb-danger',
            tone === 'neutral' && 'text-tg-text',
          )}
        >
          <MotionNumber value={value} format="usd" mode="smooth" />
        </p>
      ) : (
        <p className="pb-numeral mt-1 text-[22px] text-tg-hint">—</p>
      )}
    </div>
  );
}

function Bid({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | null;
  tone: 'success' | 'danger';
}) {
  return (
    <div
      className={clsx(
        'flex flex-1 items-center justify-between rounded-pb-md border px-3 py-2 text-[13px]',
        tone === 'success'
          ? 'border-pb-success/40 bg-pb-success-soft text-pb-success'
          : 'border-pb-danger/40 bg-[color-mix(in_oklab,var(--pb-danger)_14%,transparent)] text-pb-danger',
      )}
    >
      <span className="font-medium">{label}</span>
      {value !== null ? (
        <MotionNumber
          value={value}
          format="number"
          mode="smooth"
          className="!font-medium"
        />
      ) : (
        <span>—</span>
      )}
    </div>
  );
}
